import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import VehicleResultCard from './VehicleResultCard';
import { VehicleMake, VehicleModel } from '../../services/vehicleApi';

interface VehicleSearchResult {
  make: VehicleMake;
  model: VehicleModel;
  availableShops: number;
  nearestShopDistance?: number;
  compatibleTyres: number;
  relevanceScore: number;
}

interface AdvancedVehicleResultsProps {
  results: VehicleSearchResult[];
  isLoading: boolean;
  onVehicleSelect: (result: VehicleSearchResult) => void;
  onRequestQuote: (result: VehicleSearchResult) => void;
  onBookService: (result: VehicleSearchResult) => void;
  className?: string;
}

type SortOption = 'relevance' | 'popularity' | 'distance' | 'availability' | 'name';
type ViewMode = 'grid' | 'list';

const AdvancedVehicleResults: React.FC<AdvancedVehicleResultsProps> = ({
  results,
  isLoading,
  onVehicleSelect,
  onRequestQuote,
  onBookService,
  className = ''
}) => {
  const [sortBy, setSortBy] = useState<SortOption>('relevance');
  const [filterByAvailability, setFilterByAvailability] = useState(false);
  const [filterByPopular, setFilterByPopular] = useState(false);
  const [maxDistance, setMaxDistance] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showFilters, setShowFilters] = useState(false);

  // Filter and sort results
  const filteredAndSortedResults = useMemo(() => {
    let filtered = [...results];

    // Apply filters
    if (filterByAvailability) {
      filtered = filtered.filter(result => result.availableShops > 0);
    }

    if (filterByPopular) {
      filtered = filtered.filter(result => result.make.popular);
    }

    if (maxDistance !== null) {
      filtered = filtered.filter(result => 
        !result.nearestShopDistance || result.nearestShopDistance <= maxDistance
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'relevance':
          return b.relevanceScore - a.relevanceScore;
        case 'popularity':
          return Number(b.make.popular) - Number(a.make.popular);
        case 'distance':
          const distA = a.nearestShopDistance || Infinity;
          const distB = b.nearestShopDistance || Infinity;
          return distA - distB;
        case 'availability':
          return b.availableShops - a.availableShops;
        case 'name':
          return `${a.make.name} ${a.model.name}`.localeCompare(`${b.make.name} ${b.model.name}`);
        default:
          return 0;
      }
    });

    return filtered;
  }, [results, sortBy, filterByAvailability, filterByPopular, maxDistance]);

  const getSortLabel = (option: SortOption) => {
    const labels = {
      relevance: 'Best Match',
      popularity: 'Most Popular',
      distance: 'Nearest First',
      availability: 'Most Available',
      name: 'Alphabetical'
    };
    return labels[option];
  };

  const getResultsStats = () => {
    const total = results.length;
    const filtered = filteredAndSortedResults.length;
    const available = filteredAndSortedResults.filter(r => r.availableShops > 0).length;
    const popular = filteredAndSortedResults.filter(r => r.make.popular).length;

    return { total, filtered, available, popular };
  };

  const stats = getResultsStats();

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Loading Header */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
          <div className="flex space-x-4 mb-6">
            <div className="h-10 bg-gray-200 rounded w-32"></div>
            <div className="h-10 bg-gray-200 rounded w-24"></div>
            <div className="h-10 bg-gray-200 rounded w-28"></div>
          </div>
        </div>

        {/* Loading Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
              <div className="h-48 bg-gray-200"></div>
              <div className="p-6 space-y-4">
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-12 bg-gray-200 rounded"></div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                </div>
                <div className="flex space-x-2">
                  <div className="h-10 bg-gray-200 rounded flex-1"></div>
                  <div className="h-10 bg-gray-200 rounded flex-1"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Results Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Search Results
            <span className="ml-2 text-lg font-normal text-gray-600">
              ({stats.filtered} of {stats.total} vehicles)
            </span>
          </h2>
          <div className="flex flex-wrap items-center space-x-4 mt-2 text-sm text-gray-600">
            <span className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              {stats.available} available nearby
            </span>
            <span className="flex items-center">
              <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
              {stats.popular} popular brands
            </span>
          </div>
        </div>

        {/* View Controls */}
        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              showFilters
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
            </svg>
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-50 rounded-2xl p-6 overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {(['relevance', 'popularity', 'distance', 'availability', 'name'] as SortOption[]).map(option => (
                    <option key={option} value={option}>
                      {getSortLabel(option)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Availability Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Availability
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filterByAvailability}
                    onChange={(e) => setFilterByAvailability(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Available nearby only</span>
                </label>
              </div>

              {/* Popular Brands Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Type
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filterByPopular}
                    onChange={(e) => setFilterByPopular(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Popular brands only</span>
                </label>
              </div>

              {/* Distance Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Distance (km)
                </label>
                <input
                  type="number"
                  placeholder="Any distance"
                  value={maxDistance || ''}
                  onChange={(e) => setMaxDistance(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Clear Filters */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setSortBy('relevance');
                  setFilterByAvailability(false);
                  setFilterByPopular(false);
                  setMaxDistance(null);
                }}
                className="text-sm text-gray-600 hover:text-gray-800 underline"
              >
                Clear all filters
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results Grid/List */}
      {filteredAndSortedResults.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">No vehicles found</h3>
          <p className="text-gray-600 mb-4">
            Try adjusting your filters or search criteria
          </p>
          <button
            onClick={() => {
              setFilterByAvailability(false);
              setFilterByPopular(false);
              setMaxDistance(null);
            }}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Clear filters
          </button>
        </motion.div>
      ) : (
        <div className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
            : 'space-y-4'
        }>
          {filteredAndSortedResults.map((result, index) => (
            <motion.div
              key={`${result.make.id}-${result.model.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className={viewMode === 'list' ? 'max-w-none' : ''}
            >
              <VehicleResultCard
                make={result.make}
                model={result.model}
                availableShops={result.availableShops}
                nearestShopDistance={result.nearestShopDistance}
                compatibleTyres={result.compatibleTyres}
                onViewDetails={() => onVehicleSelect(result)}
                onRequestQuote={() => onRequestQuote(result)}
                onBookService={() => onBookService(result)}
                className={viewMode === 'list' ? 'flex-row' : ''}
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdvancedVehicleResults;